/* eslint-disable no-nested-ternary */
import React, { useCallback, useState } from 'react'
import {
	Button,
	Card,
	Table,
	Typography,
	Input,
	Tabs,
	message,
	Popconfirm,
	Modal,
	Form,
} from 'antd'
import { Template } from 'types'
import moment from 'moment'
import {
	DeleteOutlined,
	LeftCircleOutlined,
	SyncOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import getFileNameFromURL from 'utils/getFileNameFromURL'
import syncTemplateStatus from 'api/template/syncTemplateStatus'
import rejectTemplateStatus from 'api/template/rejectTemplateStatus'
import deleteTemplate from 'api/template/deleteTemplate'
import ReactJson from 'react-json-view'
import styles from './styles.module.scss'

const { Title } = Typography
const { TextArea } = Input
const { TabPane } = Tabs

interface PropTypes {
	loading: boolean
	data: Template | null
	onStatusChange: Function
}

const OverviewTable: React.FC<PropTypes> = ({
	data,
	loading,
	onStatusChange,
}) => {
	const navigate = useNavigate()
	const [visible, setVisible] = useState(false)
	const [confirmLoading, setConfirmLoading] = useState(false)

	/**
	 * Back to Templates Button
	 */
	const handleBack = () => {
		if (window.history.state && window.history.state.idx > 0) {
			navigate(-1)
		} else {
			navigate('/templates')
		}
	}

	const getFile = useCallback((template: Template | null) => {
		if (template !== null) {
			if (template.type === 'TEXT') {
				return null
			}
			const link =
				template.type === 'IMAGE'
					? template.image?.link || ''
					: template.type === 'VIDEO'
					? template.video?.link || ''
					: template.type === 'DOCUMENT'
					? template.document?.link || ''
					: ''
			const fileName =
				template.type === 'DOCUMENT'
					? template.document?.fileName || ''
					: template.type === 'IMAGE'
					? template.image?.fileName ||
					  getFileNameFromURL(template.image?.link || '')
					: template.type === 'VIDEO'
					? template.video?.fileName ||
					  getFileNameFromURL(template.video?.link || '')
					: ''
			return { link, fileName }
		}
		return {}
	}, [])

	/**
	 * Table Columns
	 */
	const columns = [
		{ title: 'Fields', dataIndex: 'title', key: 'title', width: '200px' },
		{ title: 'Details', dataIndex: 'dataIndex', key: 'dataIndex' },
	]
	/**
	 * Table Data for Single Template
	 */
	const overViewData = [
		{
			title: 'Label',
			dataIndex: data?.label,
		},
		{
			title: 'Category',
			dataIndex: data?.category,
		},
		{
			title: 'Element Name',
			dataIndex: data?.elementName,
		},
		{
			title: 'Status',
			dataIndex: data?.status,
		},
		{
			title: 'Client Organization',
			dataIndex: data?.client ? data.client.organizationName : '--',
		},
		{
			title: 'Client Email',
			dataIndex: data?.client ? data.client.email : '--',
		},
		{
			title: 'Created At',
			dataIndex: moment(data?.createdAt).format('DD/MM/YYYY'),
		},
		{
			title: 'Language',
			dataIndex: data?.language ? data.language.label : '--',
		},
		{
			title: 'Type',
			dataIndex: data?.type,
		},
		{
			title: 'File',
			dataIndex:
				getFile(data) !== null ? (
					<Button
						style={{ padding: 0, fontSize: '15px', height: 'auto' }}
						type='link'
						href={getFile(data)?.link}
						target='_blank'
					>
						{getFile(data)?.fileName}
					</Button>
				) : (
					'--'
				),
		},
		{
			title: 'Format Template',
			dataIndex: (
				<TextArea value={data?.formattedTemplate} rows={4} autoSize readOnly />
			),
		},
		{
			title: 'Sample Message',
			dataIndex: (
				<TextArea value={data?.sampleMessage} rows={4} autoSize readOnly />
			),
		},
		{
			title: 'Remarks',
			dataIndex: data?.remarks ? data.remarks : '--',
		},
		{
			title: 'Action',
			dataIndex: data?.action ? data.action : '--',
		},
		{
			title: 'Vendor',
			dataIndex: data?.vendor,
		},
	]

	const handleSync = async () => {
		setConfirmLoading(true)
		if (data !== null) {
			try {
				await syncTemplateStatus(data?._id)
				onStatusChange(true)
				setConfirmLoading(false)
				message.success('Template Sync successfully!')
			} catch (error) {
				setConfirmLoading(false)
			}
		} else {
			onStatusChange(false)
			setConfirmLoading(false)
		}
	}

	const handleDelete = async () => {
		if (data !== null) {
			try {
				await deleteTemplate(data?._id)
				onStatusChange(true)
				message.success('Template Successfully Delete')
			} catch (error) {
				message.error('Something went wrong')
			}
		}
	}

	const showModal = () => {
		setVisible(true)
	}

	const onRejectFinish = async (values: { remarks: string }) => {
		setConfirmLoading(true)
		if (data !== null) {
			try {
				await rejectTemplateStatus(data._id, values)
				onStatusChange(true)
				setConfirmLoading(false)
				message.success('Template Rejected')
				setVisible(false)
			} catch (error) {
				setConfirmLoading(false)
				message.error('Temlate does not reject!')
			}
		} else {
			onStatusChange(false)
			setConfirmLoading(false)
		}
	}

	const handleCancleReject = () => {
		setVisible(false)
		setConfirmLoading(false)
	}

	return (
		<Card
			title={<Title level={3}>{data?.label}</Title>}
			className={styles.overViewCard}
			extra={
				<>
					{data !== null ? (
						data.status === 'DELETE_REQUEST' ? (
							<Popconfirm
								title='Are you sure you want to delete this template?'
								onConfirm={handleDelete}
								okText='Confirm'
								cancelText='Cancel'
								okButtonProps={{
									style: { background: '#ff4d4f', borderColor: '#ff4d4f' },
								}}
							>
								<Button
									type='primary'
									danger
									icon={<DeleteOutlined />}
									loading={loading}
								>
									Delete
								</Button>
							</Popconfirm>
						) : null
					) : null}

					{data?.status === 'REQUEST_TO_APPROVE' || data?.status === 'PENDING' ? (
						<Button
							type='primary'
							icon={<SyncOutlined />}
							onClick={handleSync}
							loading={confirmLoading}
						>
							Sync
						</Button>
					) : null}
					{data?.status === 'REQUEST_TO_APPROVE' ? (
						<>
							<Button type='primary' danger onClick={showModal}>
								Reject
							</Button>
							<Modal
								title='Reject'
								visible={visible}
								onCancel={handleCancleReject}
								okButtonProps={{
									style: {
										display: 'none',
									},
								}}
							>
								<Form
									name='remarksForm'
									initialValues={{ remarks: '' }}
									onFinish={onRejectFinish}
									autoComplete='off'
									layout='vertical'
								>
									<Form.Item
										label='Remarks'
										name='remarks'
										rules={[{ required: true, message: 'Please enter remark' }]}
									>
										<Input />
									</Form.Item>
									<Form.Item>
										<Button type='primary' htmlType='submit' loading={confirmLoading}>
											Submit
										</Button>
									</Form.Item>
								</Form>
							</Modal>
						</>
					) : null}
					<Button type='primary' icon={<LeftCircleOutlined />} onClick={handleBack}>
						Back
					</Button>
				</>
			}
		>
			<Tabs type='card'>
				<TabPane tab='Overview' key='1'>
					<Table
						columns={columns}
						dataSource={overViewData}
						loading={loading}
						pagination={false}
					/>
				</TabPane>
				<TabPane tab='JSON' key='2'>
					<ReactJson
						src={data || {}}
						theme='google'
						indentWidth={8}
						style={{
							padding: 16,
							borderRadius: 8,
						}}
					/>
				</TabPane>
			</Tabs>
		</Card>
	)
}

export default OverviewTable
